/**
 * Invoices List Page
 * Main page component for viewing and managing invoices
 * US-INV-001: View Invoices List with Search and Filters
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useInvoices } from './hooks/useInvoices';
import { useInvoiceFilters } from './hooks/useInvoiceFilters';
import { InvoiceFiltersBar } from './components/InvoiceFiltersBar';
import { InvoiceFilterDrawer } from './components/InvoiceFilterDrawer';
import { InvoicesList } from './components/InvoicesList';
import { InvoiceStatus } from '@/types/invoice';
import { useSalesUnitFilter } from '@/hooks/useSalesUnitFilter';

export const InvoicesListPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');

  // Sales unit filtering
  const { salesUnitIds, shouldFilter } = useSalesUnitFilter();

  // Initialize filters hook
  const filters = useInvoiceFilters();

  // Fetch invoices with current filters and pagination
  const { data, isLoading, isError, error, refetch } = useInvoices({
    page,
    size: 20,
    searchTerm: filters.searchTerm,
    statuses: filters.statuses,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    sortBy: filters.sortBy,
    sortDirection: filters.sortDirection,
    salesUnitIds: shouldFilter ? salesUnitIds : undefined,
  });

  const handleSearchChange = (searchTerm: string) => {
    filters.setSearchTerm(searchTerm);
    setPage(0); // Reset to first page on search
  };

  const handleStatusToggle = (status: InvoiceStatus) => {
    filters.toggleStatus(status);
    setPage(0); // Reset to first page on filter change
  };

  const handleSortChange = (sortBy: string, sortDirection: 'asc' | 'desc') => {
    filters.setSortBy(sortBy as any);
    filters.setSortDirection(sortDirection);
    setPage(0); // Reset to first page on sort change
  };

  const handleViewModeChange = (mode: 'list' | 'table') => {
    setViewMode(mode);
  };

  const handleDateRangeChange = (from: string, to: string) => {
    filters.setDateRange(from, to);
    setPage(0); // Reset to first page on filter change
  };

  const handleClearFilters = () => {
    filters.clearFilters();
    setPage(0); // Reset to first page
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeliveryManagement = (invoiceId: number) => {
    // For now, navigate to delivery form
    // Note: In a real implementation, you'd fetch the related order from invoice
    console.log('[InvoicesListPage] Delivery management for invoice:', invoiceId);

    toast.info('Delivery Management', {
      description: `Navigate to delivery management for invoice #${invoiceId}`,
    });

    // Placeholder: In future, fetch order from invoice and navigate to delivery form
    // navigate(`/delivery/new?invoiceId=${invoiceId}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Invoices</h1>
            <p className="text-slate-600 mt-1">Manage and track your invoices</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetch()}
              disabled={isLoading}
              title="Refresh invoices"
            >
              <RotateCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={() => navigate('/invoices/create')}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              New Invoice
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 bg-white border-slate-200">
            <div className="text-sm font-medium text-slate-600">Total Invoices</div>
            <div className="text-2xl font-bold text-slate-900 mt-2">
              {data?.totalElements || 0}
            </div>
          </Card>
          <Card className="p-4 bg-white border-slate-200">
            <div className="text-sm font-medium text-slate-600">Paid</div>
            <div className="text-2xl font-bold text-green-600 mt-2">
              {data?.invoices.filter(inv => inv.status === 'PAID').length || 0}
            </div>
          </Card>
          <Card className="p-4 bg-white border-slate-200">
            <div className="text-sm font-medium text-slate-600">Pending Payment</div>
            <div className="text-2xl font-bold text-amber-600 mt-2">
              {data?.invoices.filter(inv =>
                inv.status === 'ISSUED' || inv.status === 'PARTIALLY_PAID' || inv.status === 'OVERDUE'
              ).length || 0}
            </div>
          </Card>
        </div>

        {/* Filters Bar */}
        <Card className="p-4 mb-6 bg-white border-slate-200">
          <InvoiceFiltersBar
            filters={filters}
            onSearchChange={handleSearchChange}
            onStatusToggle={handleStatusToggle}
            onDateRangeChange={handleDateRangeChange}
            onClearFilters={handleClearFilters}
            onOpenMobileFilters={() => setIsMobileFiltersOpen(true)}
            activeFilterCount={filters.activeFilterCount}
            onSortChange={handleSortChange}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
          />
        </Card>

        {/* Invoices List */}
        <InvoicesList
          data={data}
          isLoading={isLoading}
          isError={isError}
          error={error}
          onViewDetails={(invoiceId) => navigate(`/invoices/${invoiceId}`)}
          onDeliveryManagement={handleDeliveryManagement}
          onPageChange={handlePageChange}
          currentPage={page}
          viewMode={viewMode}
        />

        {/* Mobile Filter Drawer */}
        <InvoiceFilterDrawer
          open={isMobileFiltersOpen}
          onOpenChange={setIsMobileFiltersOpen}
          filters={filters}
          onStatusToggle={handleStatusToggle}
          onDateRangeChange={handleDateRangeChange}
          onClearFilters={handleClearFilters}
        />
      </div>
    </div>
  );
};

export default InvoicesListPage;
